import React, { useState } from 'react';
import { Box, TextField, Button, Alert, CircularProgress, Typography, Tabs, Tab } from '@mui/material';
import AttemptInputGroup from './AttemptInputGroup';
import { parseAttemptValue, buildAttemptsByTypeFromSession } from '../../utils/testStats';
import { createTestingSession, updateTestingSession } from '../../services/athleteService';

function buildInitialAttempts(testTypes, existingSession) {
  // Reconstruct exactly (preserving attempt_number as index, gaps and all),
  // then pad each test type's array up to its recommended attempt count for
  // display — padding only appends trailing blanks, so it never disturbs
  // the position of an existing attempt.
  const reconstructed = buildAttemptsByTypeFromSession(existingSession, testTypes.map(t => t.key));
  const byType = {};
  testTypes.forEach(t => {
    const existing = (reconstructed[t.key] || []).map(v => (v === null || v === undefined ? '' : String(v)));
    byType[t.key] = existing.length >= t.recommended_attempts
      ? existing
      : [...existing, ...Array.from({ length: t.recommended_attempts - existing.length }, () => '')];
  });
  return byType;
}

function hasValidAttempt(values) {
  return (values || []).some(v => parseAttemptValue(v) !== null);
}

/**
 * Shared form for both "Log Testing Session" (create) and the secondary
 * "Edit Session" flow (update) — same component, different mode. Drills are
 * tabs (one visible at a time) so a field-side user isn't scrolling through
 * every test to reach the one they're running; Save Session is a single
 * sticky action for the whole session regardless of which tab is active.
 */
export default function TestingSessionForm({ athleteId, testTypes, existingSession, onSaved, onCancel }) {
  const isEdit = Boolean(existingSession);
  const [testDate, setTestDate] = useState(existingSession?.test_date || new Date().toISOString().slice(0, 10));
  const [attemptsByType, setAttemptsByType] = useState(() => buildInitialAttempts(testTypes, existingSession));
  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const hasAnyValidAttempt = Object.values(attemptsByType).some(hasValidAttempt);

  const handleSave = async () => {
    setError(null);
    if (!testDate) { setError('Test date is required.'); return; }
    if (!hasAnyValidAttempt) { setError('Enter at least one valid attempt for at least one test.'); return; }

    const normalized = {};
    Object.entries(attemptsByType).forEach(([key, values]) => {
      normalized[key] = values.map(parseAttemptValue);
    });

    setSaving(true);
    try {
      if (isEdit) {
        await updateTestingSession(existingSession.id, { testDate, attemptsByType: normalized });
      } else {
        await createTestingSession({ athleteId, testDate, attemptsByType: normalized });
      }
      onSaved?.();
    } catch (e) {
      setError(e.message || 'Failed to save testing session.');
    } finally {
      setSaving(false);
    }
  };

  const activeType = testTypes[activeTab];

  return (
    <Box sx={{ pb: 10 /* clears the sticky action bar */ }}>
      <Typography sx={{ color: '#6a8aa8', fontSize: '0.8rem', mb: 2 }}>
        Personal testing — part of {isEdit ? "this athlete's" : "your athlete's"} own history, separate from official team results.
      </Typography>

      <TextField
        type="date"
        label="Test date"
        size="small"
        value={testDate}
        onChange={e => setTestDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        inputProps={{ max: new Date().toISOString().slice(0, 10) }}
        sx={{ mb: 2.5, width: 200 }}
      />

      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          mb: 2.5,
          borderBottom: '1px solid #1e2a3a',
          minHeight: 44,
          '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 700, fontSize: '0.85rem', px: 2 },
        }}
      >
        {testTypes.map(t => (
          <Tab
            key={t.key}
            label={hasValidAttempt(attemptsByType[t.key]) ? `✓ ${t.display_name}` : t.display_name}
            sx={{ color: hasValidAttempt(attemptsByType[t.key]) ? '#2ea84b !important' : undefined }}
          />
        ))}
      </Tabs>

      {activeType && (
        <AttemptInputGroup
          key={activeType.key}
          testType={activeType}
          values={attemptsByType[activeType.key] || []}
          onChange={next => setAttemptsByType(prev => ({ ...prev, [activeType.key]: next }))}
        />
      )}

      {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}

      {/* Sticky action bar — Save Session is always reachable without
          scrolling through the active drill's attempts. */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          mt: 3,
          py: 1.5,
          px: 0,
          display: 'flex',
          gap: 1.5,
          alignItems: 'center',
          backgroundColor: 'rgba(7,12,22,0.96)',
          backdropFilter: 'blur(6px)',
          borderTop: '1px solid #1e2a3a',
          zIndex: 5,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          size="large"
          sx={{ textTransform: 'none', fontWeight: 800, px: 4, flex: { xs: 1, sm: 'none' } }}
        >
          {saving ? <CircularProgress size={20} sx={{ color: '#050c16' }} /> : (isEdit ? 'Save Changes' : 'Save Session')}
        </Button>
        {onCancel && (
          <Button onClick={onCancel} disabled={saving} sx={{ textTransform: 'none', color: '#6a8aa8' }}>
            Cancel
          </Button>
        )}
      </Box>
    </Box>
  );
}
